import { Component, DestroyRef, ElementRef, OnInit, afterRenderEffect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TopMenuComponent } from '../shared/top-menu/top-menu.component';
import { MatIconModule } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import {
  DataDTO,
  ItemWithMonthDTO,
  ItemWithYearDTO,
  StatusLinkDTO,
  StepDTO,
  groupedDataDTO,
} from '../../dto/common-dto';
import { MONTHS } from '../../common/constants';
import { StatusDataService } from '../../services/status-info.service';
import { SpinnerComponent } from '../shared/spinner/spinner.component';

/**
 * This component is used to hold the status data.
 */
@Component({
    selector: 'app-modules-status',
    imports: [TopMenuComponent, MatIconModule, TranslatePipe, SpinnerComponent, NgClass],
    standalone: true,
    templateUrl: './status.component.html',
    styleUrl: './status.component.scss'
})
export class StatusComponent implements OnInit {
  // Inject StatusDataService.
  private readonly statusDataService = inject(StatusDataService);
  // Inject the host element to measure the rendered descriptions.
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  // Inject DestroyRef.
  private readonly destroyRef = inject(DestroyRef);

  // Array with the grouped data.
  readonly groupedArray = signal<groupedDataDTO[]>([]);
  // Stores the expanded state of each description section.
  readonly expandedDescriptions = signal<Record<string, boolean>>({});
  // Stores the ids of the descriptions that overflow their collapsed height (these get a "View more/less" button).
  readonly overflowingDescriptions = signal<ReadonlySet<string>>(new Set());
  // Stores the loader state.
  readonly loader = signal(false);

  /**
   * Runs when Angular instantiates the component.
   */
  constructor() {
    // Re-measure the descriptions every time the grouped data has been rendered.
    afterRenderEffect(() => {
      this.groupedArray();
      untracked(() => this.checkDescriptionOverflows());
    });
  }

  /**
   * A lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   */
  ngOnInit() {
    this.groupDataByDate();
  }

  /**
   * Group the data by date (year and month).
   */
  groupDataByDate(): void {
    this.loader.set(true);
    this.statusDataService
      .getStatusData()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        if (data && data.length > 0) {
          const groupedArray: groupedDataDTO[] = [];
          const groupedData: { [key: string]: ItemWithYearDTO[] } = {};
          const newArr = data.map((item, index) => ({
            year: new Date(item.date).getFullYear(),
            originalIndex: index,
            ...item,
          }));

          // Sort newArr by date in descending order (newest first).
          // For entries with the same timestamp, show the latest entry from the data source first.
          newArr.sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime() || b.originalIndex - a.originalIndex
          );

          newArr.forEach((item) => {
            if (!groupedData[item.year]) {
              groupedData[item.year] = [];
            }
            groupedData[item.year].push(item);
          });

          for (const year in groupedData) {
            const data = groupedData[year];
            const groupedDataByMonth: { [key: string]: ItemWithMonthDTO[] } =
              {};

            data.forEach((item: ItemWithYearDTO) => {
              const month = new Date(item.date).getMonth();
              if (!groupedDataByMonth[month]) {
                groupedDataByMonth[month] = [];
              }
              const itemWithMonth: ItemWithMonthDTO = {
                ...item,
                month,
              };
              groupedDataByMonth[month].push(itemWithMonth);
            });

            const groupedDataArray: DataDTO[] = Object.entries(
              groupedDataByMonth
            )
              .map(([month, data]) => ({
                month,
                data: data.sort(
                  (a, b) =>
                    new Date(b.date).getTime() - new Date(a.date).getTime() || b.originalIndex - a.originalIndex
                ),
              }))
              .sort((a, b) => Number(b.month) - Number(a.month));

            groupedArray.unshift({
              year,
              data: groupedDataArray,
            });
          }

          // Sort groupedArray by year in descending order.
          groupedArray.sort((a, b) => Number(b.year) - Number(a.year));
          this.groupedArray.set(groupedArray);
        }
        this.loader.set(false);
      });
  }

  /**
   * Convert a number to a month.
   *
   * @param num Represents the number of the month.
   *
   * @returns The month name.
   */
  convertNumberToMonth(num: string): string {
    return MONTHS[Number(num)];
  }

  /**
   * Convert a date string to the user's local timezone.
   * Old dates without timezone info are assumed to be Copenhagen time (CET/CEST).
   *
   * @param date Represents the date.
   *
   * @returns The date in the format dd Mon yyyy, hh:mm TZ (e.g. 12 Mar 2026, 09:00 CET).
   */
  convertDate(date: string): string {
    let dateStr = date;
    // Old dates have no timezone info — assume they were entered in Copenhagen time.
    const hasTimezone = /Z|[+-]\d{2}:\d{2}/.test(date);
    if (!hasTimezone) {
      const copenhagenOffset = this.getCopenhagenOffset(date);
      dateStr = date + copenhagenOffset;
    }
    const newDate = new Date(dateStr);
    const formatter = new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZoneName: 'short',
    });
    return formatter.format(newDate);
  }

  /**
   * Get the UTC offset for Europe/Copenhagen at a given date.
   *
   * @param dateStr Represents a date string without timezone.
   *
   * @returns The offset string (e.g. "+01:00" for CET, "+02:00" for CEST).
   */
  private getCopenhagenOffset(dateStr: string): string {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Copenhagen',
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date(dateStr + 'Z'));
    const tz = parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
    const match = tz.match(/GMT([+-])(\d+)/);
    if (match) {
      return match[1] + match[2].padStart(2, '0') + ':00';
    }
    return '+01:00';
  }

  /**
   * Toggle the description height.
   *
   * @param uniqueId Represents the id of the element.
   */
  toggleDescription(uniqueId: string): void {
    this.expandedDescriptions.update((expanded) => ({
      ...expanded,
      [uniqueId]: !expanded[uniqueId],
    }));
  }

  /**
   * Measure the rendered descriptions and record which ones overflow their collapsed height.
   */
  checkDescriptionOverflows(): void {
    const overflowing = new Set<string>();
    this.host.nativeElement
      .querySelectorAll<HTMLElement>('.step-description')
      .forEach((element) => {
        if (element.scrollHeight > 60) {
          overflowing.add(element.id);
        }
      });
    this.overflowingDescriptions.set(overflowing);
  }

  /**
   * Gets the unique id of the description.
   *
   * @param year Represents the year.
   * @param month Represents the month.
   * @param index Represents the index of the description.
   *
   * @returns The unique id of the description as string.
   */
  getDescriptionId(year: string, month: string, index: number): string {
    return `${year}-${month}-${index}`;
  }

  /**
   * Format a description string as HTML, wrapping bullet lines in elements
   * with hanging indentation so wrapped text aligns past the bullet symbol.
   *
   * @param description The raw description text.
   *
   * @returns HTML string with bullet lines wrapped in div.bullet-line.
   */
  formatDescription(description: string): string {
    const escaped = description
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

    return escaped
      .split('\n')
      .map((line) => {
        if (/^\s*[\u2022*-]\s/.test(line)) {
          return `<div class="bullet-line">${line}</div>`;
        }
        return `<div>${line || '&nbsp;'}</div>`;
      })
      .join('');
  }

  /**
   * Check whether a status link is safe to render. Only http(s) URLs with
   * non-empty text are allowed.
   *
   * @param link The link to validate.
   *
   * @returns True if the link is valid, false otherwise.
   */
  isValidLink(link: StatusLinkDTO): boolean {
    if (!link || typeof link.text !== 'string' || typeof link.url !== 'string') {
      return false;
    }
    if (link.text.trim() === '' || link.url.trim() === '') {
      return false;
    }
    try {
      const parsed = new URL(link.url);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  }

  /**
   * Get the valid links for a step, filtered through isValidLink and with the
   * url trimmed so the value bound to href matches the one that was validated.
   * Returns an empty array when the step has no links field or when links is
   * not an array (the data comes from untrusted external JSON).
   *
   * @param step The status step.
   *
   * @returns The filtered links array with normalized urls.
   */
  validLinks(step: StepDTO): StatusLinkDTO[] {
    return (Array.isArray(step.links) ? step.links : [])
      .filter((link) => this.isValidLink(link))
      .map((link) => ({ ...link, url: link.url.trim() }));
  }
}
